import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { PresetOut } from '@/api/types';
import { paths } from '@/app/paths';
import { Icon, Pill } from '@/design-system';
import { STATUS_TONE } from './labels';
import { PresetArt } from './PresetArt';
import s from './library.module.css';

type Props = {
  preset: PresetOut;
  name: string;
  /** the card's position: the design staggers the shimmer by 0.3 s per card */
  index: number;
  onFavorite: () => void;
  onAdd: () => void;
  /** set when «Добавить в структуру» cannot pin here: the button keeps the design's look, says why */
  addHint?: string;
};

/** A library card (design_markup.html `libCards`): the picture with its star, name, version, tags, status and «Добавить в структуру». */
export function PresetCard({ preset: p, name, index, onFavorite, onAdd, addHint }: Props) {
  const { t } = useTranslation('library');
  return (
    <div className={s.card} data-testid="preset-card">
      <div className={s.art}>
        <div className={s.shimmer} style={{ animationDelay: `${index * 0.3}s` } as CSSProperties} />
        <PresetArt preset={p} />
        <button
          type="button"
          className={s.star}
          title={t('card.favorite')}
          aria-label={t('card.favorite')}
          aria-pressed={p.is_favorite}
          style={{ color: p.is_favorite ? 'var(--acc)' : 'var(--fg-5)' }}
          onClick={onFavorite}
        >
          <Icon name="star" size={14} />
        </button>
      </div>
      <div className={s.body}>
        <div className={s.titleRow}>
          <Link to={paths.preset(p.id)} className={s.name} aria-label={t('card.open', { name })}>
            {name}
          </Link>
          {p.version !== null && <span className={s.version}>{t('card.version', { n: p.version })}</span>}
        </div>
        <div className={s.tags}>
          <span className={s.typeChip}>{p.section_type}</span>
          {p.tags.map((tag) => (
            <span key={tag} className={s.tag}>
              {tag}
            </span>
          ))}
          <Pill tone={STATUS_TONE[p.status]} size="tagTight" className={s.status}>
            {t(`status.${p.status}`)}
          </Pill>
        </div>
        <button type="button" className={s.add} aria-disabled={addHint ? true : undefined} title={addHint} onClick={onAdd}>
          {t('card.add')}
          <Icon name="plus" size={14} />
        </button>
      </div>
    </div>
  );
}
